import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAllPaymentLinks } from '../../actions/paymentLink';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Alert,
    Spinner,
    Table,
    Badge,
    InputGroup
} from 'react-bootstrap';
import {
    Link2,
    Copy,
    CheckCircle,
    RefreshCw,
    Search,
    Plus,
    Calendar,
    User,
    IndianRupee,
    Eye,
    ExternalLink
} from 'lucide-react';

const PaymentLinksList = ({ getAllPaymentLinks, paymentLinks, loading, error, pagination }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [count, setCount] = useState(10);
    const [latest, setLatest] = useState('true');
    const [searchTerm, setSearchTerm] = useState('');
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        document.title = 'Payment Links List';
        fetchPaymentLinks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, count, latest]);

    const fetchPaymentLinks = () => {
        getAllPaymentLinks({
            page: currentPage,
            count: count,
            latest: latest
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCountChange = (e) => {
        setCount(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSortChange = (e) => {
        setLatest(e.target.value);
        setCurrentPage(1);
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            created: { variant: 'info', label: 'Created' },
            issued: { variant: 'primary', label: 'Issued' },
            paid: { variant: 'success', label: 'Paid' },
            partially_paid: { variant: 'warning', label: 'Partially Paid' },
            cancelled: { variant: 'danger', label: 'Cancelled' },
            expired: { variant: 'secondary', label: 'Expired' }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', label: status || 'Unknown' };
        return <Badge bg={config.variant}>{config.label}</Badge>;
    };

    const filteredPaymentLinks = paymentLinks.filter((link) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const orderIdStr = link.orderId?.orderId?.toString() || link.orderId?._id?.toString() || '';
        return (
            orderIdStr.toLowerCase().includes(search) ||
            link.customer?.name?.toLowerCase().includes(search) ||
            link.customer?.email?.toLowerCase().includes(search) ||
            link.paymentLinkUrl?.toLowerCase().includes(search) ||
            link.paymentLink?.toLowerCase().includes(search)
        );
    });

    const renderPagination = () => {
        if (!pagination || pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (startPage > 1) {
            items.push(
                <Button
                    key={1}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className="me-1"
                >
                    1
                </Button>
            );
            if (startPage > 2) {
                items.push(<span key="ellipsis-start" className="me-1">...</span>);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Button
                    key={number}
                    variant={number === currentPage ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => handlePageChange(number)}
                    className="me-1"
                >
                    {number}
                </Button>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<span key="ellipsis-end" className="me-1">...</span>);
            }
            items.push(
                <Button
                    key={pagination.pages}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Button>
            );
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4 gap-2">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Previous
                </Button>
                {items}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                >
                    Next
                </Button>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-primary fw-bold">
                                    <Link2 size={32} className="me-3" />
                                    Payment Links
                                </h1>
                                <p className="text-muted mb-0">
                                    View and manage all Razorpay payment links
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    onClick={fetchPaymentLinks}
                                    disabled={loading}
                                >
                                    <RefreshCw size={16} className="me-2" />
                                    Refresh
                                </Button>
                                <Button
                                    variant="primary"
                                    as={Link}
                                    to="/inventory/v1.2/payment-link"
                                >
                                    <Plus size={16} className="me-2" />
                                    Create Payment Link
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <Card className="shadow-sm border-0 bg-primary text-white">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.total || 0}</div>
                                        <div className="small">Total Payment Links</div>
                                    </div>
                                    <Link2 size={28} className="opacity-75" />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <Card className="shadow-sm border-0 bg-info text-white">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.pages || 0}</div>
                                        <div className="small">Total Pages</div>
                                    </div>
                                    <Calendar size={28} className="opacity-75" />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <Card className="shadow-sm border-0 bg-success text-white">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.page || 1}</div>
                                        <div className="small">Current Page</div>
                                    </div>
                                    <Eye size={28} className="opacity-75" />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <Card className="shadow-sm border-0 bg-warning text-white">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{filteredPaymentLinks?.length || 0}</div>
                                        <div className="small">Showing</div>
                                    </div>
                                    <Search size={28} className="opacity-75" />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Filters */}
                <Row className="mb-4">
                    <Col md={6}>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Search size={16} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by order ID, customer, email, or payment link..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            ×
                                        </Button>
                                    )}
                                </InputGroup>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Form.Select
                                    value={count}
                                    onChange={handleCountChange}
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </Form.Select>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Form.Select
                                    value={latest}
                                    onChange={handleSortChange}
                                >
                                    <option value="true">Newest First</option>
                                    <option value="false">Oldest First</option>
                                </Form.Select>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading payment links...</p>
                        </div>
                    </div>
                )}

                {/* Payment Links Table */}
                {!loading && (
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-table me-2"></i>
                                Payment Links
                            </h5>
                            {pagination && (
                                <Badge bg="light" text="dark" className="fs-6">
                                    {pagination.total || 0} total
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            {filteredPaymentLinks && filteredPaymentLinks.length > 0 ? (
                                <>
                                    <div className="table-responsive">
                                        <Table striped hover responsive className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Payment Link ID</th>
                                                    <th>Order ID</th>
                                                    <th>Customer</th>
                                                    <th>Amount (₹)</th>
                                                    <th>Amount Paid (₹)</th>
                                                    <th>Status</th>
                                                    <th>Payment Link</th>
                                                    <th>Created At</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPaymentLinks.map((link) => (
                                                    <tr key={link._id}>
                                                        <td>
                                                            <span className="fw-bold text-info">
                                                                {link.paymentLink || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Link
                                                                to={`/inventory/v1.2/orders/${link.orderId?._id || 'N/A'}`}
                                                                className="text-decoration-none fw-bold text-primary"
                                                            >
                                                                {link.orderId?.orderId || link.orderId?._id || 'N/A'}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <div className="fw-bold">
                                                                <User size={14} className="me-1" />
                                                                {link.customer?.name || 'N/A'}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {link.customer?.email || 'N/A'}
                                                            </div>
                                                            {link.customer?.phone && (
                                                                <div className="small text-muted">
                                                                    {link.customer.phone}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-success">
                                                                ₹{link.amount?.toLocaleString() || '0.00'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-info">
                                                                ₹{link.amountPaid?.toLocaleString() || '0.00'}
                                                            </span>
                                                        </td>
                                                        <td>{getStatusBadge(link.status)}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <a
                                                                    href={link.paymentLinkUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-decoration-none small"
                                                                    style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                                >
                                                                    {link.paymentLinkUrl}
                                                                </a>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => copyToClipboard(link.paymentLinkUrl, link._id)}
                                                                    title="Copy link"
                                                                >
                                                                    {copied === link._id ? (
                                                                        <CheckCircle size={14} />
                                                                    ) : (
                                                                        <Copy size={14} />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={14} className="me-1 text-muted" />
                                                                {formatDate(link.createdAt)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group" role="group">
                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    as={Link}
                                                                    to={`/inventory/v1.2/payment-links/${link._id}`}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    href={link.paymentLinkUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title="Open Payment Link"
                                                                >
                                                                    <ExternalLink size={14} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {renderPagination()}
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <Link2 size={48} className="text-muted mb-3" />
                                    <h4 className="text-muted mb-3">No Payment Links Found</h4>
                                    <p className="text-muted mb-4">
                                        {searchTerm ? 'No payment links match your search criteria.' : 'Get started by creating your first payment link.'}
                                    </p>
                                    {!searchTerm && (
                                        <Button
                                            variant="primary"
                                            as={Link}
                                            to="/inventory/v1.2/payment-link"
                                        >
                                            <Plus size={16} className="me-2" />
                                            Create Payment Link
                                        </Button>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

PaymentLinksList.propTypes = {
    getAllPaymentLinks: PropTypes.func.isRequired,
    paymentLinks: PropTypes.array,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    pagination: PropTypes.object
};

const mapStateToProps = (state) => ({
    paymentLinks: state.paymentLink.paymentLinks,
    loading: state.paymentLink.loading,
    error: state.paymentLink.error,
    pagination: state.paymentLink.pagination
});

export default connect(mapStateToProps, { getAllPaymentLinks })(PaymentLinksList);

